import React from 'react'
import PropTypes from 'prop-types'

import grey from '@material-ui/core/colors/grey'

// UI
import { withStyles } from '@material-ui/core/styles'
import Button from '@material-ui/core/Button'
import LinearProgress from '@material-ui/core/LinearProgress'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'

// Icons
import CloudUpload from '@material-ui/icons/CloudUpload'
import FileDownload from '@material-ui/icons/FileDownload'

// Import libraries
import _ from 'lodash'
import fileDownload from 'js-file-download'
import filesize from 'filesize'
import Dropzone from 'react-dropzone'
import Papa from 'papaparse'

// Import country converter
import * as countries from 'i18n-iso-countries';

import { BATCH_SIZE, LOG_EVERY, MAX_ROWS } from '../../constants'

import MatchAPI from '../../api/MatchAPI'

// Regular expressions for fields
const FLD_UID = /u?id|ref(erence)|locid|locnum/i
const FLD_ADDRESS = /query|address|street|town|city|locality|state/i
const FLD_POSTCODE = /((?:post|zip|eir)(?:al)?[\s_]?code|zip)/i
const FLD_COUNTRY = /country|iso/i

const styles = theme => ({
  root: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
  },
  paper: { position: 'absolute', textAlign: 'center',
    top: 75, right: 10, bottom: 10, left: 10 },
  container: { height: '100%' },
  upload: { margin: theme.spacing.unit * 2, padding: theme.spacing.unit * 2 },
  dropzone: { position: 'relative', width: '100%', height: '100%' },
  overlay: { position: 'absolute', top: 0, right: 0, bottom: 0, left: 0,
    background: grey[500], textAlign: 'center', vAlign: 'center'
  },
  overlayText: { color: '#fff' },
  results: { margin: theme.spacing.unit * 2, padding: theme.spacing.unit * 2 },
})

const getProgress = progress => {
  const { processed, total, complete } = progress
  if (complete) return 100
  if (!processed || !total) return 0
  const pct = Math.round(parseFloat((progress.processed / progress.total).toFixed(2)) * 100)
  return pct
}

const getFields = fields => {
  if (!fields) throw new Error('No fields to parse!')
  let res = {
    address: [],
    postcode: '',
    country: ''
  }
  for (let field of fields) {
    if (!res.uid && FLD_UID.test(field)) res.uid = field
    else if (FLD_ADDRESS.test(field)) res.address.push(field)
    else if (!res.postcode && FLD_POSTCODE.test(field)) res.postcode = field
    else if (!res.country && FLD_COUNTRY.test(field)) res.country = field
  }
  return res
}

const formatQuery = (row, fields) => {
  if (!row || !fields.address) return
  const addressLines = fields.address.map(field => row[field])
  let query = addressLines.join(',')
  if (fields.postcode && row[fields.postcode]) {
    query = query + ' ' + row[fields.postcode]
  }
  return query
}

const isCsv = file => {
  return file && (_.includes(['text/plain', 'text/csv', 'application/csv',
    'application/vnd.ms-excel'], file.type) ||
    (file.name && file.name.split('.').pop() === 'csv'))
}

class Batch extends React.Component {

  matchAPI
  dropzoneRef
  requests = []

  constructor() {
    super()
    this.state = {
      minSize: 10,
      maxSize: 1000000,
      files: [],
      dropzoneActive: false,
      progress: {},
      results: []
    }
    countries.registerLocale(require("i18n-iso-countries/langs/en.json"))
  }

  onDragEnter() {
    this.setState({
      dropzoneActive: true
    })
  }

  onDragLeave() {
    this.setState({
      dropzoneActive: false
    })
  }

  onDrop(files, rejected) {
    this.setState({
      files,
      error: (!_.isEmpty(rejected) || !isCsv(files[0])) && 'Files must be less than 1mb in CSV format, please try another',
      dropzoneActive: false,
      progress: {},
      results: []
    })
    files.forEach(file => {
      const reader = new FileReader()
      reader.onload = () => this.processFile(reader.result)
      reader.onerror = () => this.setState({ error: 'The file could not be read, please try another' })
      reader.readAsText(file)
    })
  }

  processFile(file, opts) {
    this.requests = []
    this.setState({ results: [] })
    Papa.parse(file, {
      dynamicTyping: true,
      header: true,
      trimHeader: true,
      complete: (results) => {
        const { data, errors, meta } = results
        if (errors && errors[0] && ['TooFewFields', 'UndetectableDelimiter'].indexOf(errors[0].code) === -1) {
          this.setState({ error: errors[0].message })
          return
        }
        if (data.length <=1 ) {
          this.setState({ error: 'The file does not contain any rows to process' })
          return
        }
        if (data.length > MAX_ROWS) {
          this.setState({ error: `A maximum of ${MAX_ROWS.toLocaleString()} addresses can be processed, please try another file` })
          return
        }
        const fields = getFields(meta.fields)
        if (!fields) {
          this.setState({ error: 'No known fields were detected, please try another file' })
          return
        }
        if (!fields.uid) {
          this.setState({ error: 'No known identified field detected, please try another file' })
          return
        }
        if (!fields.address) {
          this.setState({ error: 'No known address field detected, please try another file' })
          return
        }
        try {
          this.geocodeFile(data, fields)
        } catch (error) {
          console.error(error)
          this.setState({ error: 'An unknown error has occurred please contact support' })
        }
      },
      error: error => {
        console.error(error)
        this.setState({ error })
      }
    })
  }

  async geocodeFile(data, fields) {
    const { profile } = this.props
    const { prefs } = profile
    const { defaults } = prefs
    // DEBUG && console.log(JSON.stringify(prefs, null, 2))
    let results = []
    const start = new Date()
    this.setState({ progress: { processed: 0, total: data.length, complete: false  } })
    for (let i = 0; i < data.length; i += BATCH_SIZE) {
      const queries = data.slice(i, i + BATCH_SIZE)
      const responses = await Promise.all(queries.map(async request => {

        const uid = request[fields.uid]
        const query = formatQuery(request, fields)
        let country = defaults.country
        if (fields.country && request[fields.country]) {
          country = request[fields.country].toLowerCase()
          if (_.includes(['united kingdom','great britain','uk','northern ireland'], country)) {
            country = 'gb'
          }
          else {
            let alpha2 = countries.getAlpha2Code(country, 'en')
            if (alpha2) country = alpha2.toLowerCase()
          }
        }
        if (!uid && !query) return
        if (!uid) return { ...request, error: 'A unique identifier (uid) must be supplied for every address' }
        if (!query) return { ...request, error: 'At least one address field must be supplied' }
        if (country && country.length !== 2) return { ...request, error: 'Country must be a 2 character ISO code' }
        // DEBUG && console.log(JSON.stringify({ query, country, ...defaults }, null, 2))
        if (!defaults.intel && _.get(prefs, 'output.intel')) defaults.intel = true
        try {
          const result = await this.matchAPI.geocode({ query, country, ..._.omit(defaults, 'country') })
          
          return { request, result }
        } catch (err) {
          // Handle errors
          console.error(err)

          return {
            request, 
            result: {
              match_status: _.get(err, 'response.data.message', err.message)
            }
          }
        }
      }))
      responses.map(response => response && results.push(this.formatRow(response)))
      i % LOG_EVERY === 0 && this.setState({ progress: { processed: i, total: data.length, complete: false  } })
    }
    const took = Math.round((new Date() - start) / 1000)
    this.setState({ files: [], progress: { total: data.length, complete: true, took }, results })
  }

  formatRow(response) {
    const { profile } = this.props
    const { request, result, error } = response
    const { input, match_status, address, intel } = _.get(profile, 'prefs.output', {})
    let out = { }
    if (input) {
      for (let key of _.keys(input)) {
        out[input[key]] = _.get(request, key)
      }
    } else {
      out = { ...request }
    }
    if (!result || !result.match_status) {
      out.error = (error || _.get(result, 'message', 'Unknown error'))
      return out
    } else {
      out[match_status] = result.match_status
    }
    if (result && result.match_status === 'MATCH') {
      const res = _.get(result, 'results[0]', {})
      for (let key of _.keys(address)) {
        if (out[address[key]]) {
          const property = _.get(res, key)
          if (property) out[address[key]] = out[address[key]] + ', ' + property
        } else {
          if (key === 'properties.country') {
            const alpha2 = _.get(res, key)
            out[address[key]] = countries.getName(alpha2, "en", {select: "official"})
          } else {
            out[address[key]] = _.get(res, key)
          }
        }
      }
      const int = _.get(res, 'intel.results', {})
      for (let key of _.keys(intel)) {
        out[intel[key]] = _.get(int, key)
      }
    }
    out.error = ''
    return out
  }

  downloadResults() {
    const { results } = this.state
    const fields = _.without([ ...new Set([].concat(...results.map(result => _.keys(result)))) ], 'error')
    fileDownload(Papa.unparse({ fields, data: results, quotes: true }), 'results.csv')
  }

  render() {

    const { classes, apiKey, clientId, profile } = this.props
    const { error, files, maxSize, minSize, dropzoneActive, progress, results } = this.state

    // Instantiate the APIs if not already setup
    if ((!this.matchAPI) &&
      _.get(profile, 'name')) {
      this.matchAPI = new MatchAPI(apiKey, clientId, profile.name)
    }

    return (
      <div className={classes.root}>
        <Paper className={classes.paper} elevation={4}>
          <div className={classes.container}>
            <div className={classes.upload}>
              { dropzoneActive && <div className={classes.overlay}>
                <Typography variant='caption' className={classes.overlayText}>Drop files...</Typography>
              </div> }
              <Dropzone
                ref={el => { this.dropzoneRef = el }}
                className={classes.dropzone}
                minSize={minSize} maxSize={maxSize}
                onDrop={this.onDrop.bind(this)}
                onDragEnter={this.onDragEnter.bind(this)}
                onDragLeave={this.onDragLeave.bind(this)}
              >
                <Typography align='center'><CloudUpload color='secondary' style={{ fontSize: 256, marginBottom: -20 }} /></Typography>
                <Typography variant="headline" component="h4" gutterBottom>Drag and drop a CSV file</Typography>
                { error && <Typography align='center' color='error' gutterBottom>{error}</Typography> }
              </Dropzone>
              <Button variant='raised' color='default' onClick={() => { this.dropzoneRef.open() }} style={{ marginBottom: 20 }}>OR SELECT A FILE TO UPLOAD</Button>
              <Typography align='center' gutterBottom> An example can be found <a href='https://cdn.addresscloud.com/assets/csv/template.csv'>here</a></Typography>
            </div>
            { progress && (progress.processed >= 0 || progress.complete) &&
              <div className={classes.results}>
                <LinearProgress variant='determinate' color='primary' value={getProgress(progress)} />
                { files && files[0] && <Typography variant='subheading' align='center' style={{ margin: 20 }}>{ !progress.complete ? 'Processing' : 'Processed' } file: {files[0].name} ({filesize(files[0].size)})</Typography> }
                { !progress.complete ?
                  <Typography variant='subheading' align='center' style={{ margin: 10 }}>{getProgress(progress)}% complete</Typography> :
                  <Typography variant='subheading' align='center' style={{ margin: 10 }}>Completed in {progress.took} seconds</Typography>
                }
                { results && results.length > 0 &&
                  <Button variant='raised' color='secondary' onClick={() => this.downloadResults()} style={{ margin: 20 }}>
                    <FileDownload className={classes.leftIcon} />
                    Download {progress.total} Results
                  </Button>
                }
              </div>
            }
          </div>
        </Paper>
      </div>
    )
  }
}

Batch.propTypes = {
  classes: PropTypes.object.isRequired,
  apiKey: PropTypes.string.isRequired,
  clientId: PropTypes.string.isRequired,
  profile: PropTypes.object.isRequired
}

export default withStyles(styles)(Batch)
